import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountProductEnum,
  AnalyticsEventProperty,
  BottomSheetElementResult,
  IllustrationStyleDictionary,
  PresentBottomSheetAction,
  SlBarGraphData,
  SlCalloutBanner,
  SlFilterItemDataView,
  SlLineGraphData,
  SmartChartData,
  SmartViewDataView,
  Typenames,
  VxlColorType,
} from '@varo/graphql-schema-types';
import { getSourceName } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import CalloutBanner from 'features/accounts/SmartViewContent/CalloutBanner/CalloutBanner';
import SmartViewBalance from 'features/accounts/SmartViewContent/SmartViewBalance/SmartViewBalance';
import SmartViewTransactions, {
  SmartViewRef,
} from 'features/accounts/SmartViewContent/SmartViewTransactions/SmartViewTransactions';
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { VXLButton } from 'shared/components/sdui/VXLButton';
import { VXLSpacing } from 'shared/components/sdui/VXLSpacing';
import { VXLTextView } from 'shared/components/sdui/VXLText';
import IncomeChart from 'shared/components/SmartViews/Charts/IncomeChart';
import SpendingChart from 'shared/components/SmartViews/Charts/SpendingChart';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import HorizontalScrollView from 'shared/components/vxl/HorizontalScroll/HorizontalScroll';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import FilterChip from 'shared/components/vxl/selection/FilterChip/FilterChip';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

interface SmartViewContentProps {
  smartViewData: SmartViewDataView | undefined | null;
  smartViewDataLoading: boolean;
  accountId: string;
  fetchSmartViewTransactions: (e: string | undefined) => void;
  accountType: AccountProductEnum;
  selectedTab?: string;
}

type SmartViewContentInterface = SmartViewContentProps & {
  dataRef: React.Ref<SmartViewRef>;
};

const SmartViewContent = ({
  smartViewData,
  smartViewDataLoading,
  accountId,
  dataRef,
  fetchSmartViewTransactions,
  accountType,
  selectedTab,
}: SmartViewContentInterface) => {
  const defaultSubCategory =
    smartViewData?.filters?.filters?.[0]?.categoryId ||
    smartViewData?.views?.[0]?.categoryId ||
    null;

  const [subCategoryView, setSubCategoryView] = useState<SmartChartData>();
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(
    defaultSubCategory,
  );
  const [callout, setCallout] = useState<SlCalloutBanner>();
  const infoClickRef = useRef<BottomSheetModal>(null);

  const infoIconPressEvent = usePressableAnalytics({
    analyticsId: 'infoIcon',
    customAttributes: {
      source_name: selectedFilterId
        ? (getSourceName(selectedFilterId) as string)
        : '',
      information_type: 'total_monthly_spend',
      account_type: accountType.toLowerCase(),
      category:
        accountType === AccountProductEnum.Checking
          ? accountType.toLowerCase()
          : 'varo_believe',
    },
  });

  const infoClickContent = (
    subCategoryView?.infoClick as PresentBottomSheetAction
  )?.elements;

  useEffect(() => {
    if (defaultSubCategory) {
      setSelectedFilterId(defaultSubCategory);
      setSubCategoryView(
        smartViewData?.views?.filter(
          (view) => view.categoryId === defaultSubCategory,
        )[0],
      );
      setCallout(
        smartViewData?.callouts?.filter(
          (callout) => callout.categoryId === defaultSubCategory,
        )[0],
      );
    }
  }, [defaultSubCategory, smartViewData]);

  const handleSelect = (filter: SlFilterItemDataView) => {
    fetchSmartViewTransactions(filter.categoryId);
    setSelectedFilterId(filter.categoryId);
    setSubCategoryView(
      smartViewData?.views?.filter(
        (view) => view.categoryId === filter.categoryId,
      )[0],
    );
    setCallout(
      smartViewData?.callouts?.filter(
        (callout) => callout.categoryId === filter.categoryId,
      )[0],
    );
  };

  const handleIconClick = () => {
    infoIconPressEvent();
    infoClickRef?.current?.present();
  };

  const bottomSheetButtonClick = () => {
    infoClickRef?.current?.close();
  };

  const chartKey = `${subCategoryView?.graph.__typename}-${subCategoryView?.categoryId}`;

  const reduceArrayToObject = (array?: AnalyticsEventProperty[]) => {
    return array?.reduce(
      (obj: { [key: string]: string }, item) =>
        Object.assign(obj, { [item.key]: item.value }),
      {},
    );
  };

  return (
    <View style={styles.container}>
      {smartViewDataLoading ? (
        <View style={styles.balanceContainer} testID="loader">
          <LoadingSpinner />
        </View>
      ) : (
        <View>
          <View style={styles.balanceContainer} testID="smartViewBalance">
            {subCategoryView ? (
              <SmartViewBalance
                title={subCategoryView.title}
                balanceAmount={subCategoryView.copy}
                onInfoClick={handleIconClick}
                hasInfoIcon={isNotNil(subCategoryView.infoClick)}
              />
            ) : null}
          </View>
          <View style={styles.chartLineContainer} testID="smartViewGraph">
            {subCategoryView?.graph?.__typename ===
            Typenames.SLLineGraphData ? (
              <SpendingChart
                key={`SpendingChart-${chartKey}`}
                data={subCategoryView.graph as SlLineGraphData}
              />
            ) : null}

            {subCategoryView?.graph?.__typename === Typenames.SLBarGraphData ? (
              <IncomeChart
                key={`IncomeChart-${chartKey}`}
                data={subCategoryView.graph as SlBarGraphData}
              />
            ) : null}
          </View>
          {smartViewData?.filters?.filters?.length ? (
            <View style={styles.filterContainer}>
              <BodySmBoldText style={styles.filterTitle}>
                {smartViewData.filters.title}
              </BodySmBoldText>

              <HorizontalScrollView
                contentContainerStyle={styles.filters}
                testID="filterChipContainer"
              >
                {smartViewData.filters.filters.map(
                  (filter: SlFilterItemDataView) => {
                    const imageSource = (
                      filter?.filterIcon as IllustrationStyleDictionary
                    )?.fallbackURL
                      ? {
                          uri: (
                            filter?.filterIcon as IllustrationStyleDictionary
                          )?.fallbackURL,
                        }
                      : undefined;

                    return (
                      <View key={filter?.categoryId}>
                        <FilterChip
                          labelText={filter?.title}
                          captionText={filter?.detail as string}
                          imageSource={imageSource}
                          colorIndicator={
                            (filter?.filterIcon as VxlColorType)?.color
                          }
                          isDisabled={false}
                          isSelected={selectedFilterId === filter?.categoryId}
                          analyticsId="smartViewCategory"
                          customAttributes={{
                            ...reduceArrayToObject(
                              filter?.clickAnalytics?.properties,
                            ),
                            category:
                              accountType === AccountProductEnum.Checking
                                ? accountType.toLowerCase()
                                : 'varo_believe',
                          }}
                          onPress={() => handleSelect(filter)}
                        />
                      </View>
                    );
                  },
                )}
              </HorizontalScrollView>
            </View>
          ) : null}
          {callout ? (
            <View style={styles.calloutBannerContainer} testID="calloutBanner">
              <CalloutBanner
                message={callout.text.copy}
                iconKey={callout.iconV2}
              />
            </View>
          ) : null}
          <BottomSheet ref={infoClickRef}>
            <View style={styles.modal}>
              {infoClickContent?.map((element: BottomSheetElementResult) => {
                return (
                  <View key={(element as unknown as { id: string })?.id}>
                    {element?.__typename === Typenames.FullWidthTextComponent
                      ? VXLTextView(element)
                      : null}
                    {element?.__typename === Typenames.SpacingComponent
                      ? VXLSpacing(element)
                      : null}
                    {element?.__typename === Typenames.ButtonComponent ? (
                      <VXLButton
                        key={element.id}
                        component={element}
                        onPress={bottomSheetButtonClick}
                      />
                    ) : null}
                  </View>
                );
              })}
            </View>
          </BottomSheet>
        </View>
      )}
      <SmartViewTransactions
        accountId={accountId}
        categoryId={selectedFilterId}
        ref={dataRef}
        accountType={accountType}
        smartViewDataLoading={smartViewDataLoading}
        selectedTab={selectedTab}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 4,
    paddingBottom: 0,
    padding: theme.spacing.m,
  },
  calloutBannerContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  chartLineContainer: {
    backgroundColor: theme.color.background.primary,
  },
  container: {
    backgroundColor: theme.color.background.surface,
    gap: theme.spacing.s,
  },
  filterContainer: {
    backgroundColor: theme.color.background.primary,
  },
  filterTitle: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  modal: {
    backgroundColor: theme.color.background.primary,
    borderRadius: theme.spacing.s,
    paddingBottom: theme.spacing.m,
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
  },
});

export default SmartViewContent;
